import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { Subscription } from 'rxjs';
import { ProjectService } from 'src/app/services/project.service';
import Project from 'src/app/interfaces/Project';
import { Location } from '@angular/common';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PromptService } from 'src/app/services/prompt.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
})
export class EditProjectComponent implements OnInit, OnDestroy {
  isNewProject = false;
  approveMembers = false;
  step = 0;
  subscriptions: Subscription = new Subscription();
  currentUser;
  project: Project = {
    approaches: [],
    projectName: null,
    owner: null,
    ownerMeta: {
      name: null,
    },
    tags: [],
    summary: null,
    poc: {
      firstName: null,
      lastName: null,
      email: null,
      webpage: null,
    },
    timestamp: null,
    bannerImage: null,
    id: null,
  };
  editorModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  tags = {
    standard: [
      'technology',
      'environmental-impacts',
      'governance',
      'markets-finance',
      'funding',
      'cdr-community',
      'social-licensing',
    ],
    approaches: ['microalgae', 'blue-carbon', 'macroalgal','alkalinity', 'co2-removal', 'storage', 'other'],
  };
  projectMembers = [];

  projects$;
  projects: Array<Project>;
  filteredProjects: Array<any>;
  selectedProject: Project = null;
  searchInput: string = null;

  errorMsg = '';

  previewFile;
  attachmentFile;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private projectService: ProjectService,
    private location: Location,
    private router: Router,
    private renderer: Renderer2,
    private toastService: ToastService,
    private uploadService: UploadService,
    private loadingService: LoadingService,
    private promptService: PromptService,
    private notificationService: NotificationsService,
    private urlService: UrlService
  ) {}

  ngOnInit() {
    // See if new project
    this.route.queryParams.subscribe((params) => {
      if (params['new']) {
        this.isNewProject = params['new'];
      } else {
        if (params['id']) {
          this.projectService.getCurrentProject(params['id']).subscribe((p) => {
            this.project = p[0];
            this.projectService.getProjectMembers(this.project.id).subscribe((m) => {
              this.projectMembers = m;
            });
          });
        }
        if (params['approveMembers']) {
          this.approveMembers = true;
        }
      }
    });

    // Get user
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        }
      })
    );

    this.getProjects();
  }
  getProjects() {
    this.projects$ = this.projectService.getProjects().subscribe((p) => {
      this.projects = p;
      this.filteredProjects = p;

      this.filteredProjects.forEach((p) => {
        this.projectService.getProjectMembers(p.id).subscribe((m) => {
          p.members = m.length;
        });
      });
    });
  }

  filterProjectNames(input) {
    this.filteredProjects = this.projects.filter((p) => {
      if (p.projectName) {
        return p.projectName.toLowerCase().indexOf(input.toLowerCase()) !== -1;
      }
    });
  }

  selectProject(p) {
    this.selectedProject = p;
    this.searchInput = p.projectName;
    if (this.currentUser.projects.indexOf(this.selectedProject.id) !== -1) {
      this.errorMsg =
        this.currentUser.projectRoles[this.selectedProject.id].status === 'pending'
          ? "You've already sent a request to join this project!"
          : "You're already a member of this project!";
    }
  }
  clearSelectedProject() {
    this.selectedProject = null;
    this.searchInput = null;
    this.errorMsg = '';
    const projectNameInput = this.renderer.selectRootElement('.project-name-wrap input');
    setTimeout(() => projectNameInput.focus(), 0);
  }
  nextStep() {
    // if project exists, add project to user
    if (this.selectedProject) {
      // if user is not already a member of this project
      if (this.currentUser.projects.indexOf(this.selectedProject.id) === -1) {
        this.loadingService.startLoading();
        this.currentUser.projects.push(this.selectedProject.id);
        this.currentUser.projectRoles[this.selectedProject.id] = {
          role: 'member',
          status: 'pending',
        };
        this.userService.saveUser(this.currentUser.uid, this.currentUser).then(() => {
          this.toastService.setToast('<b>Success!</b> We’ve sent your request to Join Mangrove Restoration.');
          this.loadingService.stopLoading();
          // this.router.navigateByUrl(`/projects/${this.selectedProject.id}`);
          this.router.navigate(['/projects/' + this.selectedProject.id]);
        });
      }

      // if project doesn't exist, create new project
    } else {
      this.loadingService.startLoading();
      this.projects$.unsubscribe();
      this.projectService.createProject(this.project).then(
        (id: string) => {
          this.project.id = id;
          this.project.projectName = this.searchInput;
          this.project.owner = this.currentUser.uid;
          this.project.ownerMeta.name = this.currentUser.displayName;
          this.project.poc = {
            firstName: this.currentUser.firstName,
            lastName: this.currentUser.lastName,
            email: this.currentUser.email,
            webpage: this.currentUser.webpage,
          };
          this.project.timestamp = new Date();
          this.currentUser.projects.push(id);
          this.currentUser.projectRoles[id] = {
            role: 'admin',
            status: 'active',
          };
          this.projectMembers = [this.currentUser];
          this.loadingService.stopLoading();
          this.step++;
          this.getProjects();
        },
        (e) => console.error(e)
      );
    }
  }

  onFileChanged(event) {
    if (event.target.files && event.target.files[0]) {
      var reader = new FileReader();
      var t = this;
      reader.onload = function (e) {
        t.previewFile = reader.result;
      };

      reader.readAsDataURL(event.target.files[0]); // convert to base64 string

      this.attachmentFile = event.target.files[0];
    }
  }

  removePhoto() {
    this.previewFile = null;
    this.attachmentFile = null;
    this.project.bannerImage = null;
  }

  toggleApproach(a) {
    if (this.project.approaches.indexOf(a) === -1) {
      this.project.approaches.push(a);
    } else {
      this.project.approaches.splice(this.project.approaches.indexOf(a), 1);
    }
  }
  toggleTag(t) {
    if (this.project.tags.indexOf(t) === -1) {
      this.project.tags.push(t);
    } else {
      this.project.tags.splice(this.project.tags.indexOf(t), 1);
    }
  }

  removeUser(m) {
    this.promptService.setPrompt({
      hidden: false,
      heading: 'Are you sure you want to remove ' + m.firstName + '?',
      body: null,
      confirmText: "Yes, I'm sure",
      rejectText: 'Nevermind',
    });
    this.promptService.userResponse.subscribe((res) => {
      if (res === 'confirm') {
        this.loadingService.startLoading();
        let user = { ...m };
        delete user.projectRoles[this.project.id];
        user.projects.splice(user.projects.indexOf(this.project.id));
        user.optionsToggled = false;
        this.userService.saveUser(user.uid, user).then(() => {
          this.loadingService.stopLoading();
          this.promptService.clearPrompt();
        });
      } else if (res === 'reject') {
        this.promptService.clearPrompt();
      }
    });
  }

  toggleAdmin(m) {
    this.loadingService.startLoading();
    let user = { ...m };
    user.projectRoles[this.project.id].role = user.projectRoles[this.project.id].role === 'admin' ? 'member' : 'admin';
    user.optionsToggled = false;
    this.userService.saveUser(m.uid, user).then(() => {
      m.projectRoles[this.project.id].role = user.projectRoles[this.project.id].role === 'admin' ? 'member' : 'admin';
      m.optionsToggled = !m.optionsToggled;
      this.loadingService.stopLoading();
    });
  }

  approveRequest(m) {
    this.loadingService.startLoading();
    let user = { ...m };
    user.projectRoles[this.project.id].status = 'active';
    user.optionsToggled = false;
    this.userService.saveUser(m.uid, user).then(() => {
      m.projectRoles[this.project.id].status = 'active';
      m.optionsToggled = !m.optionsToggled;
      this.loadingService.stopLoading();
      this.notificationService.sendNotifications({
        type: 'approveRequest',
        name: this.project.projectName,
        user: m.uid,
        url: `/projects/${this.project.id}`,
      });
      this.notificationService.saveNotification(
        m.uid,
        this.currentUser.uid,
        `approved your request to join <a href="/projects/${this.project.id}">their project</a>`,
        this.currentUser.displayName,
        this.currentUser.profilePicture
      );
    });
  }

  saveProject() {
    this.loadingService.startLoading();
    this.project.summary = this.urlService.processContent(this.project.summary);
    if (this.attachmentFile) {
      this.uploadService.uploadFile(this.attachmentFile).then((downloadUrl) => {
        this.project.bannerImage = downloadUrl;
        this.updateProject();
      });
    } else {
      this.updateProject();
    }
  }

  updateProject() {
    this.projectService.updateProject(this.project.id, this.project).then(
      (res) => {
        this.userService.saveUser(this.currentUser.uid, this.currentUser).then(() => {
          if (this.isNewProject) {
            this.notificationService.sendNotifications({
              type: 'newProjects',
              author: this.project.owner,
              title: this.project.projectName,
              url: `/projects/${this.project.id}`,
            });
          }
          this.loadingService.stopLoading();
          // this.router.navigateByUrl(`/projects/${this.project.id}`);
          this.router.navigate(['/projects/' + this.project.id]);
        });
      },
      (e) => console.error(e)
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
